import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Fuse from 'fuse.js';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useAutocompleteStyles, useTextFieldStyles } from './styles';
import CATEGORY_HIERARCHY from '../../../utils/constants/category';
import SendIcon from '@mui/icons-material/Send';
import { Avatar } from '@mui/material';
import CHAT_ICON from '../../../images/icons/AIChatIconOrange.svg';

const Search = ({ selectedFilter, setSelectedCategory, handleOpenChat }) => {
	const textFieldClasses = useTextFieldStyles();
	const [emptyInput, setEmptyInput] = useState(true);
	const autocompleteClasses = useAutocompleteStyles({ emptyInput });
	const { t } = useTranslation(['main', 'category']);
	const [value, setValue] = useState(null);
	const [inputValue, setInputValue] = useState('');
	const [open, setOpen] = useState(false);
	const [, setChatWindow] = useState(false);
	const placeholderText = t('main:components.categoriesDashboard.search.placeholder');

	const fuseInput = CATEGORY_HIERARCHY.categories.map(category => ({
		id: category.id,
		pathParam: category.pathParam,
		released: category.released,
		chartSupported: category.chartSupported,
		spiceLiveModelSupported: category.spiceLiveModelSupported,
		wdSupported: category.wdSupported,
		technology:
			typeof category.technology === 'string'
				? t('main:components.categoriesDashboard.filters.technology.items.' + category.technology + '.title')
				: category.technology.map(technology =>
						t('main:components.categoriesDashboard.filters.technology.items.' + technology + '.title')
				  ),
		abbreviation: t('category:' + category.id + '.abbreviation'),
		title: t('category:' + category.id + '.title'),
		description: t('category:' + category.id + '.description'),
		allCategoryText:
			t('category:' + category.id + '.title') +
			' ' +
			t('category:' + category.id + '.abbreviation') +
			' ' +
			t('category:' + category.id + '.description'),
		redirectParam: category.redirectParam
	}));

	const abbreviations = fuseInput.map(category => category.abbreviation);

	const fuseValues = new Fuse(fuseInput, {
		ignoreLocation: true,
		keys: [
			{ name: 'technology', weight: 0.8 },
			{ name: 'abbreviation', weight: 1 },
			{ name: 'title', weight: 0.9 },
			{ name: 'description', weight: 0.6 },
			{ name: 'allCategoryText', weight: 0.4 }
		],
		useExtendedSearch: true,
		threshold: 0.1
	});

	const fuseSearch = actInputValue => (actInputValue ? fuseValues.search(actInputValue) : []);

	const handleOpen = () => {
		if (fuseSearch(inputValue).length > 0) {
			setOpen(true);
		}
	};

	const handleInputChange = (_event, newInputValue) => {
		setInputValue(newInputValue);
		setEmptyInput(!newInputValue.length > 0);
		setOpen(newInputValue.length > 0 && fuseSearch(newInputValue).length > 0 && !value);
	};

	const handleSubmitWithEnter = event => {
		if (event.key === 'Enter') {
			handleOpenChat(inputValue);
			setChatWindow(true);
			//handleClear();
		}
	};

	const handleSubmit = () => {
		handleOpenChat(inputValue);
		setChatWindow(true);
		handleClear();
	};
	const handleClear = () => {
		setInputValue('');
		setValue(null);
	};

	useEffect(() => {
		setValue(null);
		setInputValue('');
	}, [selectedFilter]);

	const noOptionsOpenCondition = !open && inputValue.length > 0 && !abbreviations.includes(inputValue);

	return (
		<div>
			<Autocomplete
				id="HomePage__Search_input"
				fullWidth
				openOnFocusgetOptionLabel={option => option.abbreviation}
				filterOptions={(_defOptions, state) => fuseSearch(state.inputValue).map(option => option.item)}
				clearOnBlur={false}
				forcePopupIcon={false}
				selectOnFocus={false}
				options={fuseInput.map(option => option.title)}
				renderOption={option => (
					<Fragment>
						<Typography variant="subtitle2" display="inline">
							{option.abbreviation}
						</Typography>
						<Typography variant="body2" display="inline" color="textSecondary">
							{'\u00a0\u00a0' + option.title}
						</Typography>
					</Fragment>
				)}
				value={value}
				inputValue={inputValue}
				onChange={(_event, newValue, _reason) => {
					setSelectedCategory(newValue);
					setValue(newValue && Object.values(newValue).length > 0 ? Object.values(newValue)[7] : '');
					setInputValue(newValue ? newValue : '');
				}}
				onInputChange={handleInputChange}
				onBlur={() => {
					if (!value) {
						setSelectedCategory(inputValue ? 'CATEGORY_NOT_FOUND' : null);
					}
				}}
				open={open}
				onOpen={handleOpen}
				onClose={() => setOpen(false)}
				renderInput={params => (
					<TextField
						{...params}
						hiddenLabel
						variant="filled"
						placeholder={placeholderText}
						InputProps={{
							...params.InputProps,
							startAdornment: <>{params.InputProps.startAdornment}</>,
							endAdornment: (
								<>
									<InputAdornment position="end" className={autocompleteClasses.sendIconContainer}>
										<SendIcon className={autocompleteClasses.sendIcon} onClick={emptyInput ? null : handleSubmit} />
									</InputAdornment>
								</>
							)
						}}
						classes={textFieldClasses}
						label=" "
					/>
				)}
				// NoOptionsText={t('main:components.categoriesDashboard.search.noOptions')}
				onKeyDown={noOptionsOpenCondition && handleSubmitWithEnter}
				classes={autocompleteClasses}
			/>
			{noOptionsOpenCondition && (
				<span
					className={autocompleteClasses.noOptionsHint}
					onClick={() => {
						handleOpenChat(inputValue);
						setChatWindow(true);
						handleClear();
					}}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Avatar alt="AIChat" src={CHAT_ICON} sx={{ width: 35, height: 35, mr: 2 }} />
						<Typography variant="subtitle2" component="span" style={{ fontWeight: 'bold', marginRight: 4 }}>
							{t('main:components.categoriesDashboard.search.askChat')}
						</Typography>
						<Typography variant="subtitle2" component="span">
							{inputValue}
						</Typography>
					</div>
				</span>
			)}
		</div>
	);
};

Search.propTypes = {
	selectedFilter: PropTypes.object.isRequired,
	filteredCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
	setSelectedCategory: PropTypes.func.isRequired,
	handleOpenChat: PropTypes.func.isRequired
};

export default Search;
